import {
  constructFeaturebaseJwtUrl,
  isValidFeaturebaseUrl,
} from '@griply/utils';
import { jwtDecode } from 'jwt-decode';
import { redirectWithParameters } from 'shared/lib/react-router-dom';
import { Paths } from 'shared/routes/paths';
import {
  getFeaturebaseToken,
  getGoals,
  getUser,
  signInWithToken,
  signOutUser,
} from 'shared/services/backend-api';
import { getLoggableError, getRollbar } from 'shared/services/error-tracking';
import { getSearchParamIdToken } from 'shared/utils/get-search-param-id-token';
import { isProductionEnvironment } from 'shared/utils/is-environment';

export const userLoader = async () => {
  if (!isProductionEnvironment) {
    // eslint-disable-next-line no-console -- non-prod logging
    console.log(
      `[Debug-Loader] userLoader started at ${performance.now().toFixed(0)}ms`,
    );
  }

  const user = await getUser();

  if (!user) {
    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- non-prod logging
      console.warn(
        '[Debug-Loader] ❌ getUser returned null. Triggering REDIRECT to Login.',
      );
    }
    throw redirectWithParameters(Paths.Login);
  }

  if (!isProductionEnvironment) {
    // eslint-disable-next-line no-console -- non-prod logging
    console.log('[Debug-Loader] ✅ User found, proceeding.');
  }
  return user;
};

export const loginLoader = async () => {
  await signInWithToken();
  const user = await getUser();

  if (user) {
    // Check for Featurebase SSO params
    const urlSearchParams = new URLSearchParams(window.location.search);
    const isFeaturebaseSso = urlSearchParams.get('featurebase') === 'true';
    const returnTo = urlSearchParams.get('return_to');

    // If SSO params present, attempt Featurebase redirect
    if (isFeaturebaseSso && returnTo) {
      // Validate return_to domain (security: prevent open redirect)
      if (!isValidFeaturebaseUrl(returnTo)) {
        getRollbar().warning('Invalid Featurebase SSO return_to URL', {
          returnTo,
        });
        // Fall through to normal login redirect below
      } else {
        // Fetch JWT token and redirect to Featurebase
        try {
          const { token } = await getFeaturebaseToken();
          const featurebaseRedirectUrl = constructFeaturebaseJwtUrl(
            token,
            returnTo,
          );
          // External redirect -- cannot use React Router redirect() for external URLs
          window.location.href = featurebaseRedirectUrl;
          // Return never-resolving promise to prevent React Router from navigating
          return new Promise(() => {});
        } catch (error) {
          // Token fetch failed -- report to Rollbar, fall through to normal redirect
          getRollbar().error(
            'Featurebase SSO token fetch failed',
            getLoggableError(error),
          );
          // Fall through to normal login redirect below
        }
      }
    }

    const goals = await getGoals();
    let extraParams = {};
    // get the queryParameters from the start of login to go with
    try {
      const token = getSearchParamIdToken();
      const { state } = token
        ? jwtDecode<{ state: string }>(token)
        : ({} as { state: undefined });
      extraParams = state ? JSON.parse(state) : {};
    } catch (error) {
      getRollbar().error(
        'parsing searchParams from jwt token failed',
        getLoggableError(error),
      );
    }

    throw redirectWithParameters(
      goals.length ? Paths.Home : Paths.Goals,
      extraParams,
    );
  }

  return user;
};

export const completeSignupLoader = async () => {
  // make sure that if someone has login credentials it is handled
  await signInWithToken();
  const user = await getUser();

  // make sure the user is signed out before continuing
  if (user) {
    await signOutUser();
  }

  return true;
};
